import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Flex,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { ApolloClient } from '@apollo/client';
import { fetchApi } from '@codametrix/client-api';

import { Section } from './Section';
import { AssignCodersModal } from './AssignCodersModal';
import { MarkProjectCompleteDialog } from './MarkProjectCompleteDialog';
import {
  MANAGER_HOME_QUERY,
  ARCHIVE_PROJECT_MUTATION,
} from './managerHome.graphql';
import { useGroupFilterPersistence } from './useGroupFilterPersistence';
import { applyGroupFilter, applyNameFilter, sortProjects, getAuthHeaders } from './utils';
import {
  ManagerHomeGroup,
  ManagerHomeProject,
  ManagerHomeSection,
  SECTION_ORDER,
} from './types';

interface ManagerHomeQueryResult {
  managerHome: {
    manager: { userId: string; userName: string };
    groups: ManagerHomeGroup[];
    projects: ManagerHomeProject[];
  };
}

interface ArchiveToastContentProps {
  projectName: string;
  onClose: () => void;
  onUndo: () => Promise<void>;
}

const ArchiveToastContent: React.FC<ArchiveToastContentProps> = ({ projectName, onClose, onUndo }) => {
  const toast = useToast();

  const handleUndo = async () => {
    onClose();
    try {
      await onUndo();
    } catch (err) {
      console.error('Failed to restore project', err);
      toast({ title: 'Failed to restore project', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p="3" boxShadow="md">
      <Flex align="center" justify="space-between" gap="4">
        <Text fontSize="sm">"{projectName}" archived</Text>
        <Text
          as="button"
          fontSize="sm"
          fontWeight={600}
          color="blue.600"
          cursor="pointer"
          onClick={handleUndo}
        >
          Undo
        </Text>
      </Flex>
    </Box>
  );
};

const PAGE_EMPTY_COPY =
  "You don't have any projects yet. Projects appear here when you're added to a group that has active CQA projects. Talk to your admin if you expect to see projects here.";

export interface ManagerHomePageProps {
  apolloClient: ApolloClient<any>;
  onProjectSelected: (projectId: string, status?: string) => void;
  searchTerm: string;
}

export const ManagerHomePage: React.FC<ManagerHomePageProps> = ({
  apolloClient,
  onProjectSelected,
  searchTerm,
}) => {
  const { data, loading, error, refetch } = useQuery<ManagerHomeQueryResult>(MANAGER_HOME_QUERY, {
    client: apolloClient,
    fetchPolicy: 'network-only',
  });

  const [assignmentProject, setAssignmentProject] = useState<ManagerHomeProject | null>(null);
  const [completeProject, setCompleteProject] = useState<ManagerHomeProject | null>(null);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [rubricNameMap, setRubricNameMap] = useState<Map<number, string>>(new Map());
  // Completion state fetched DIRECTLY from cqa-service (no BFF). ruleId -> completedAt.
  // Presence in the map means the project is complete (response contains only completed rules).
  const [completionMap, setCompletionMap] = useState<Map<string, string | null>>(new Map());
  // CDMX-16342 (F2): true only while the completions read is failing; gates the fail-open
  // 'closed'→Completed coalesce (full rationale at the bucketing loop below).
  const [completionsUnavailable, setCompletionsUnavailable] = useState(false);
  const toast = useToast();

  const groups = data?.managerHome?.groups ?? [];
  const rawProjects = data?.managerHome?.projects ?? [];
  const availableGroupIds = useMemo(() => groups.map((g) => g.id), [groups]);
  const { selectedGroupIds, toggleGroup } = useGroupFilterPersistence(availableGroupIds);

  // Fetch all rubric names once after the main query resolves
  useEffect(() => {
    if (rawProjects.length === 0) return;
    const uniqueRubricIds = [
      ...new Set(rawProjects.map((p) => p.rubricId).filter((id): id is number => id != null)),
    ];
    if (uniqueRubricIds.length === 0) return;

    fetchApi('/api/cqa-service/v1/rubrics', {
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((rubrics: Array<{ id: number; name: string }>) => {
        const idSet = new Set(uniqueRubricIds);
        const map = new Map<number, string>();
        for (const r of rubrics) {
          if (idSet.has(r.id)) map.set(r.id, r.name);
        }
        setRubricNameMap(map);
      })
      .catch((err) => console.error('Failed to load rubric names', err));
  }, [rawProjects]);

  // Merge rubricName into project objects
  const projects = useMemo(
    () =>
      rawProjects.map((p) => ({
        ...p,
        rubricName: p.rubricId != null ? (rubricNameMap.get(p.rubricId) ?? null) : null,
      })),
    [rawProjects, rubricNameMap],
  );

  // Stable, sorted, comma-joined list of the visible projects' ruleIds — doubles as the
  // completions request body AND the effect dependency, so we only refetch when the set changes.
  const ruleIdsKey = useMemo(
    () => [...new Set(rawProjects.map((p) => p.ruleId).filter(Boolean))].sort().join(','),
    [rawProjects],
  );

  // POST the ruleIds to cqa-service (the old GET ?ruleIds=... is gone; this UI is the only caller).
  // FAIL-OPEN: on error, reset the map to empty so projects simply stay in their active sections.
  useEffect(() => {
    let cancelled = false;
    if (!ruleIdsKey) {
      // No visible rules → nothing can be complete; clear any stale entries.
      if (!cancelled) {
        setCompletionMap(new Map());
        setCompletionsUnavailable(false);
      }
      return;
    }
    const ruleIds = ruleIdsKey.split(',');
    fetchApi('/api/cqa-service/v1/project/completions', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ ruleIds }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((completions: Array<{ ruleId: string; completedAt?: string | null }>) => {
        if (cancelled) return;
        const map = new Map<string, string | null>();
        for (const c of completions) map.set(c.ruleId, c.completedAt ?? null);
        setCompletionMap(map);
        setCompletionsUnavailable(false);
      })
      .catch((err) => {
        console.error('Failed to load project completions', err);
        // Don't strand stale completed entries when the read fails.
        if (!cancelled) {
          setCompletionMap(new Map());
          setCompletionsUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ruleIdsKey]);

  const handleRowClick = (project: ManagerHomeProject) => {
    if (project.section === 'coder_complete') {
      onProjectSelected(project.id, 'CQA_COMPLETE');
    } else if (project.section === 'in_flight') {
      onProjectSelected(project.id, 'IN_CQA');
    } else if (project.section === 'completed') {
      // CDMX-16342 (F1): open a completed project on its Closed work items. mark-complete force-closes
      // its stragglers to CQA_CLOSED, but the default no-status worklist filter EXCLUDES CQA_CLOSED, so
      // without an explicit status the drill-in would hide exactly the items AC4c/AC8 require to remain
      // viewable. 'CQA_CLOSED' is a valid manager status tab (getAvailableStatuses).
      onProjectSelected(project.id, 'CQA_CLOSED');
    } else {
      onProjectSelected(project.id);
    }
  };

  const handleReassign = useCallback((project: ManagerHomeProject) => {
    setAssignmentProject(project);
  }, []);

  const handleArchive = useCallback(
    async (project: ManagerHomeProject) => {
      try {
        await apolloClient.mutate({
          mutation: ARCHIVE_PROJECT_MUTATION,
          variables: { id: project.id, qarule: { availability: 'UNAVAILABLE' } },
        });
        refetch();
        toast({
          title: `"${project.name}" archived`,
          status: 'success',
          duration: 6000,
          isClosable: true,
          render: ({ onClose }) => (
            <ArchiveToastContent
              projectName={project.name}
              onClose={onClose}
              onUndo={async () => {
                await apolloClient.mutate({
                  mutation: ARCHIVE_PROJECT_MUTATION,
                  variables: { id: project.id, qarule: { availability: 'AVAILABLE' } },
                });
                refetch();
              }}
            />
          ),
        });
      } catch (err) {
        console.error('Failed to archive project', err);
        toast({ title: 'Failed to archive project', status: 'error', duration: 3000 });
      }
    },
    [apolloClient, refetch, toast],
  );

  // Open the confirmation dialog (AC3).
  const handleMarkComplete = useCallback((project: ManagerHomeProject) => {
    setCompleteProject(project);
  }, []);

  const confirmMarkComplete = useCallback(async () => {
    if (!completeProject) return;
    const project = completeProject;
    setIsMarkingComplete(true);
    try {
      const response = await fetchApi(`/api/cqa-service/v1/project/${encodeURIComponent(project.ruleId)}/mark-complete`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!response.ok) throw new Error(`Failed to mark project complete (${response.status})`);
      const result = await response.json(); // MarkProjectCompleteResponse
      const closedCount = result?.closedCount ?? 0;
      const alreadyComplete = result?.alreadyComplete ?? false;
      // IN_CQA work items that survived the force-close mop-up. >0 means a partial close,
      // so surface it instead of implying everything is clean.
      const stillOpenCount = result?.stillOpenCount ?? 0;
      setCompleteProject(null);
      // Move the card to Completed immediately, then refetch so straggler counts refresh.
      setCompletionMap((prev: Map<string, string | null>) => {
        const next = new Map(prev);
        next.set(project.ruleId, result?.completedAt ?? null);
        return next;
      });
      refetch();
      toast({
        title: alreadyComplete
          ? `"${project.name}" was already complete`
          : `"${project.name}" completed`,
        description: alreadyComplete
          ? undefined
          : `${closedCount} work ${closedCount === 1 ? 'item' : 'items'} closed${
              stillOpenCount > 0 ? `, ${stillOpenCount} still open` : ''
            }`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Failed to mark project complete', err);
      toast({ title: 'Failed to mark project complete', status: 'error', duration: 3000 });
    } finally {
      setIsMarkingComplete(false);
    }
  }, [completeProject, refetch, toast]);

  const handleModalClose = () => {
    setAssignmentProject(null);
    refetch();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p="4rem">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="2rem">
        <Text color="red.500">{error.message}</Text>
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box data-testid="manager-home-empty" p="4rem" textAlign="center">
        <Text>{PAGE_EMPTY_COPY}</Text>
      </Box>
    );
  }

  const filtered = applyNameFilter(applyGroupFilter(projects, selectedGroupIds), searchTerm);
  const bySection = new Map<ManagerHomeSection, ManagerHomeProject[]>();
  for (const section of SECTION_ORDER) bySection.set(section, []);
  for (const project of filtered) {
    // Completion is sourced from cqa-service (not GraphQL). A completed project moves
    // OUT of its active section INTO 'completed' (AC4b); derive completed/completedAt
    // from the map so the card's Completed badge and buildStats render correctly.
    // CDMX-16342 (F2): the BFF returns a fully force-closed project as section 'closed' (a value the
    // UI SECTION_ORDER has no bucket for). If the completions read fails (fail-open empties
    // completionMap), a marker-completed project would fall through to `bySection.get('closed')` ===
    // undefined and be silently dropped at the push below. So ONLY on the fail-open path
    // (completionsUnavailable) do we coalesce a BFF 'closed' section into Completed, keeping the card
    // visible. On the success path completionMap is authoritative — an all-closed project WITHOUT a
    // marker is intentionally NOT relabeled "Completed" (that's a distinct 'closed'/16341 concern).
    const isComplete =
      completionMap.has(project.ruleId) ||
      (completionsUnavailable && (project.section as string) === 'closed');
    const effectiveSection: ManagerHomeSection = isComplete ? 'completed' : project.section;
    const enriched = isComplete
      ? {
          ...project,
          // Route by the rendered section so handleRowClick opens the completed view, not IN_CQA.
          section: effectiveSection,
          completed: true,
          completedAt: completionMap.get(project.ruleId) ?? null,
        }
      : project;
    bySection.get(effectiveSection)?.push(enriched);
  }

  return (
    <>
      <Box p="2rem" overflow="auto" height="100%">
        {/* Page header */}
        <Flex align="center" mb="1.5rem">
          <Text
            fontFamily="var(--font-heading)"
            fontSize="var(--fs-xl)"
            fontWeight={600}
            color="var(--color-text-high-emphasis)"
          >
            Project Health Dashboard
          </Text>

          <Flex align="center" gap="8px" ml="auto">
            <Text
              fontFamily="var(--font-body)"
              fontSize="0.85rem"
              color="var(--color-text-medium-emphasis)"
              fontWeight={500}
              mr="4px"
            >
              Groups:
            </Text>
            {groups.map((group) => {
              const active = selectedGroupIds.has(group.id);
              return (
                <Box
                  key={group.id}
                  as="button"
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`group-chip-${group.id}`}
                  px="10px"
                  py="4px"
                  borderRadius="var(--r-md)"
                  border="1px solid"
                  borderColor={active ? 'var(--color-chips-brown-500)' : 'var(--color-chips-grey-500)'}
                  bg={active ? 'var(--color-chips-brown-50)' : 'var(--color-chips-grey-50)'}
                  color={active ? 'var(--color-chips-brown-500)' : 'var(--color-chips-grey-500)'}
                  fontSize="0.78rem"
                  fontWeight={active ? 600 : 400}
                  cursor="pointer"
                  _hover={{
                    bg: 'var(--color-chips-brown-50)',
                    borderColor: 'var(--color-chips-brown-500)',
                    color: 'var(--color-chips-brown-500)',
                  }}
                >
                  {group.label}
                </Box>
              );
            })}
          </Flex>
        </Flex>

        {/* Sections */}
        {SECTION_ORDER.map((section) => (
          <Section
            key={section}
            section={section}
            projects={sortProjects(bySection.get(section) ?? [])}
            onRowClick={handleRowClick}
            onReassign={handleReassign}
            onArchive={handleArchive}
            onMarkComplete={handleMarkComplete}
          />
        ))}
      </Box>

      {/* Assign / Re-assign Modal */}
      {assignmentProject && (
        <AssignCodersModal
          isOpen={!!assignmentProject}
          onClose={handleModalClose}
          ruleId={assignmentProject.ruleId}
          collectionId={assignmentProject.id}
          projectName={assignmentProject.name}
          groupId={assignmentProject.groupId}
          apolloClient={apolloClient}
        />
      )}

      {/* Mark Project Complete confirmation (CDMX-16342 AC3) */}
      {completeProject && (
        <MarkProjectCompleteDialog
          isOpen={!!completeProject}
          projectName={completeProject.name}
          isSubmitting={isMarkingComplete}
          onConfirm={confirmMarkComplete}
          onCancel={() => {
            if (!isMarkingComplete) setCompleteProject(null);
          }}
        />
      )}
    </>
  );
};

ManagerHomePage.displayName = 'ManagerHomePage';
export default ManagerHomePage;
